import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  doc, 
  getDoc 
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();

// ========== BLOG OPERATIONS (READ) ==========

export const getPublishedBlogs = async () => {
  try {
    // Simplified query without composite index requirement
    // Fetch all blogs and filter/sort client-side as a workaround
    const blogsQuery = query(
      collection(db, 'blogs'),
      where('published', '==', true)
    );
    
    const snapshot = await getDocs(blogsQuery);
    const blogs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Sort by publishedAt client-side
    return blogs.sort((a, b) => {
      const dateA = a.publishedAt ? new Date(a.publishedAt) : new Date(0);
      const dateB = b.publishedAt ? new Date(b.publishedAt) : new Date(0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error fetching blogs:', error);
    throw error;
  }
};

export const getBlogBySlug = async (slug) => {
  try {
    const blogsQuery = query(
      collection(db, 'blogs'),
      where('slug', '==', slug)
    );
    
    const snapshot = await getDocs(blogsQuery);
    
    if (snapshot.empty) {
      return null;
    }
    
    const docData = snapshot.docs[0];
    const blog = { id: docData.id, ...docData.data() };
    
    // Check if published
    if (!blog.published) {
      return null;
    }
    
    return blog;
  } catch (error) {
    console.error('Error fetching blog:', error);
    throw error;
  }
};

// ========== GUIDE OPERATIONS (READ) ==========

export const getPublishedGuides = async () => {
  try {
    // Simplified query without composite index requirement
    const guidesQuery = query(
      collection(db, 'guides'),
      where('published', '==', true)
    );
    
    const snapshot = await getDocs(guidesQuery);
    const guides = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Sort by createdAt client-side
    return guides.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error('Error fetching guides:', error);
    throw error;
  }
};

export const getGuideBySlug = async (slug) => {
  try {
    const guidesQuery = query(
      collection(db, 'guides'),
      where('slug', '==', slug)
    );
    
    const snapshot = await getDocs(guidesQuery);
    
    if (snapshot.empty) {
      return null;
    }
    
    const docData = snapshot.docs[0];
    const guide = { id: docData.id, ...docData.data() };
    
    // Check if published
    if (!guide.published) {
      return null;
    }
    
    return guide;
  } catch (error) {
    console.error('Error fetching guide:', error);
    throw error;
  }
};

// ========== SITE SETTINGS (READ) ==========

export const getSiteSettings = async () => {
  try {
    const settingsRef = doc(db, 'site_settings', 'settings');
    const docSnap = await getDoc(settingsRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching site settings:', error);
    throw error;
  }
};

// ========== ADMIN OPERATIONS (WRITE via Cloud Functions) ==========

export const createBlogPost = async (blogData) => {
  const createBlog = httpsCallable(functions, 'createBlog');
  return await createBlog(blogData);
};

export const updateBlogPost = async (blogId, blogData) => {
  const updateBlog = httpsCallable(functions, 'updateBlog');
  return await updateBlog({ blogId, ...blogData });
};

export const deleteBlogPost = async (blogId) => {
  const deleteBlog = httpsCallable(functions, 'deleteBlog');
  return await deleteBlog({ blogId });
};

export const createGuidePost = async (guideData) => {
  const createGuide = httpsCallable(functions, 'createGuide');
  return await createGuide(guideData);
};

export const updateGuidePost = async (guideId, guideData) => {
  const updateGuide = httpsCallable(functions, 'updateGuide');
  return await updateGuide({ guideId, ...guideData });
};

export const deleteGuidePost = async (guideId) => {
  const deleteGuide = httpsCallable(functions, 'deleteGuide');
  return await deleteGuide({ guideId });
};

export const updateSettings = async (settingsData) => {
  const updateSiteSettings = httpsCallable(functions, 'updateSiteSettings');
  return await updateSiteSettings(settingsData);
};